import { api } from '@/lib/api'
import { defaultPaginationMeta, parseMasterCatalogResponse } from '@/lib/api/masterCatalog'

/** @typedef {import('@/types/api').Product} Product */

/**
 * @param {number | string | null | undefined} value
 */
export function toAmount(value) {
  if (value === null || value === undefined || value === '') return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * @param {number | string | null | undefined} value
 */
export function formatAmount(value) {
  return toAmount(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

/**
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<{ items: Product[], meta: import('@/types/api').PaginationMeta }>}
 */
export async function fetchProductsPage(params = {}) {
  const { data } = await api.get('/masters/products', { params })
  const parsed = parseMasterCatalogResponse(data, 'products')
  return {
    items: parsed.items,
    meta: defaultPaginationMeta(parsed.meta ?? {}),
  }
}

/**
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<Product[]>}
 */
export async function fetchProductsList(params = {}) {
  const { data } = await api.get('/masters/products', { params })
  return data.data.products
}

/**
 * @returns {Promise<Product[]>}
 */
export async function fetchProducts() {
  return fetchProductsList()
}

/**
 * POS catalog with on-hand quantities.
 * @returns {Promise<Product[]>}
 */
export async function fetchPosProducts() {
  const { data } = await api.get('/products/catalog')
  return data.data.products
}

/**
 * @param {string} barcode
 * @returns {Promise<Product>}
 */
export async function lookupProductByBarcode(barcode) {
  const { data } = await api.get('/products/lookup', { params: { barcode } })
  return data.data.product
}

/**
 * @param {number} productId
 * @returns {Promise<Product>}
 */
export async function fetchProduct(productId) {
  const { data } = await api.get(`/masters/products/${productId}`)
  return data.data.product
}

/**
 * @param {Partial<Product>} payload
 * @returns {Promise<Product>}
 */
export async function createProduct(payload) {
  const { data } = await api.post('/masters/products', payload)
  return data.data.product
}

/**
 * @param {number} productId
 * @param {Partial<Product>} payload
 * @returns {Promise<Product>}
 */
export async function updateProduct(productId, payload) {
  const { data } = await api.put(`/masters/products/${productId}`, payload)
  return data.data.product
}
